import {
    Component,
    ElementRef,
    HostListener,
    OnInit,
    Renderer2,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import * as PDFViewCtrl from '@foxitsoftware/foxit-pdf-sdk-for-web-library/lib/PDFViewCtrl.full.js';
import * as UIExtension from '@foxitsoftware/foxit-pdf-sdk-for-web-library/lib/UIExtension.full.js';
import * as Worker from '@foxitsoftware/foxit-pdf-sdk-for-web-library/lib/preload-jr-worker';
import * as Addons from '@foxitsoftware/foxit-pdf-sdk-for-web-library/lib/uix-addons/allInOne';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { __internal__ as Foxit } from '@foxitsoftware/foxit-pdf-sdk-for-web-library/lib';
import { PDFUI } from '@foxitsoftware/foxit-pdf-sdk-for-web-library/lib/UIExtension';
import { catchError, forkJoin, mergeMap, of, tap } from 'rxjs';
import { AzureLoggerService } from '../AzureLoggerService';
import { SecurityService } from '../Security.service';
import { FileSelectorService } from '../fileSelector.service';
import { Loader } from '../loaders/loader';
import { LoaderService } from '../loaders/loader.service';
import { NoLoaderService } from '../loaders/noLoader.service';
import { WindowLoaderService } from '../loaders/windowLoader.service';
import { DataSourceMeta, TextItem } from '../models';
import { fontMaps, fontMapsInfo } from './fontMaps';
import { FoxitLegacyFontDropdownSorterService } from './foxitLegacyFontDropdownSorter.service';
@Component({
    selector: 'app-foxitpdfviewer',
    templateUrl: './pdfviewer.component.html',
    styleUrls: ['./pdfviewer.component.css'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: Loader,
            useFactory: (
                ren: Renderer2,
                httpClient: HttpClient,
                securityService: SecurityService,
                logger: AzureLoggerService,
            ) => {
                if (location.hash || location.search) {
                    logger.logTrace('Default Loader was selected');
                    return new LoaderService(httpClient, securityService);
                } else if (window.opener) {
                    logger.logTrace('Window Loader was selected');
                    return new WindowLoaderService(ren);
                }
                logger.logTrace('Empty Loader was selected');
                return new NoLoaderService();
            },
            deps: [Renderer2, HttpClient, SecurityService, AzureLoggerService],
        },
    ],
})
export class PDFViewerComponent implements OnInit {
    @ViewChild('dsFieldsRef') public dsFieldsRef: ElementRef<any>;
    @ViewChild('dsTextRef') public dsTextsRef: ElementRef<any>;

    public fields: DataSourceMeta[] = [{ name: 'loading...', fields: [] }];
    public textItems: TextItem[] = [];

    private _pdfUi: PDFUI;
    private _readyWorker: any;

    public constructor(
        private _element: ElementRef,
        private renderer2: Renderer2,
        private _loader: Loader,
        private _fileSelectorService: FileSelectorService,
        private _logger: AzureLoggerService,
        private _fontDropdownSorter: FoxitLegacyFontDropdownSorterService,
    ) {
        _logger.logPageView('PdfViewer', location.href);
    }

    public appendDoc(file: File) {
        this._logger.logEvent('Append file was clicked');
        this._pdfUi
            .getPDFViewer()
            .then((v) => {
                v.getCurrentPDFDoc()
                    .insertPages({
                        destIndex: v.getCurrentPDFDoc().getPageCount(),
                        file,
                        startIndex: 0,
                        endIndex: -1,
                    })
                    .then(() => this._pdfUi.redraw());
            })
            .catch((r) => {
                this._logger.logException(r);
                alert(
                    'Please, make sure that the source document is not protected.\n\rFor this you can save a copy of this doc and try again',
                );
            });
    }

    public ngOnInit() {
        this._logger.logEvent('Initializing');
        const licenseSN = environment.foxitLicenseSerialNumber;
        const licenseKey = environment.foxitLicenseKey;
        this._readyWorker = Worker({
            workerPath: '/foxit-lib/',
            enginePath: '/foxit-lib/jr-engine/gsdk',
            fontPath: '../assets/brotli',
            fontInfoPath: '../assets/fontInfo.csv',
            brotli: {
                core: false,
            },
            licenseSN,
            licenseKey,
        });

        this._loader
            .GetPdf()
            .pipe(
                catchError(() => of(new File([], 'empty'))),
                mergeMap((pdf) => {
                    return this._initPdfUi(!!pdf).then((s) => (s && pdf) || null);
                }),
                mergeMap((pdf) => {
                    return pdf ? this._openPdf(pdf) : of(null);
                }),
                mergeMap(() => {
                    return forkJoin([
                        this._loader.GetPdfDataSource().pipe(
                            tap({
                                next: (flds) => {
                                    this.fields = flds
                                        .sort((a, b) =>
                                            (a.name || '').localeCompare(b.name || ''),
                                        )
                                        .map((fld) => {
                                            fld.fields = fld.fields.sort((a, b) =>
                                                (a || '').localeCompare(b || ''),
                                            );
                                            return fld;
                                        });
                                    if (flds && flds.length > 0) {
                                        this._pdfUi
                                            .getComponentByName('sidebar')
                                            .then((sidebar) => {
                                                const item = (sidebar as any).append(
                                                    '<sidebar-panel @tooltip tooltip-title="View Data Source fields" tooltip-placement="right"  icon-class="icon-database" title="Data Source"></sidebar-panel>',
                                                );
                                                this.renderer2.appendChild(
                                                    item.bodyElement,
                                                    this.dsFieldsRef.nativeElement
                                                        .previousSibling,
                                                );
                                            });
                                    }
                                },
                            }),
                            catchError(() => of(null)),
                        ),
                        this._loader.GetTextItems().pipe(
                            tap({
                                next: (flds) => {
                                    this.textItems = flds.sort((a, b) =>
                                        (a.name || '').localeCompare(b.name || ''),
                                    );
                                    if (flds && flds.length > 0) {
                                        this._pdfUi
                                            .getComponentByName('sidebar')
                                            .then((sidebar) => {
                                                const item = (sidebar as any).append(
                                                    '<sidebar-panel @tooltip tooltip-title="View Text Items" tooltip-placement="right"  icon-class="icon-database" title="Text Items"></sidebar-panel>',
                                                );
                                                this.renderer2.appendChild(
                                                    item.bodyElement,
                                                    this.dsTextsRef.nativeElement
                                                        .previousSibling,
                                                );
                                            });
                                    }
                                },
                            }),
                            catchError(() => of(null)),
                        ),
                        this._loader.GetAnnotations().pipe(
                            tap({
                                next: async (annotations) => {
                                    const pdfViewer = await this._pdfUi.getPDFViewer();

                                    const validStamps = annotations.filter(
                                        (stamp) =>
                                            !stamp.inactive &&
                                            !!stamp.fileType &&
                                            !!stamp.annotFile &&
                                            stamp.annotFile instanceof Blob &&
                                            !!stamp.annotType &&
                                            !!stamp.docAnnotCatName &&
                                            !!stamp.name &&
                                            !!stamp.width &&
                                            !!stamp.height &&
                                            !!stamp.stampId,
                                    );

                                    const maxCategoryLength = Math.max(
                                        ...validStamps.map(
                                            (stamp) => stamp.docAnnotCatName.length,
                                        ),
                                    );

                                    validStamps.forEach((stamp) => {
                                        const simpleStamp = {
                                            fileType: stamp.fileType.split('/').pop(),
                                            url: URL.createObjectURL(stamp.annotFile),
                                            annotType: stamp.annotType,
                                            category: stamp.docAnnotCatName.padEnd(
                                                maxCategoryLength,
                                                ' ',
                                            ),
                                            name: stamp.name,
                                            width: stamp.width,
                                            height: stamp.height,
                                            stampId: stamp.stampId,
                                        };

                                        pdfViewer.addAnnotationIcon(simpleStamp);
                                    });

                                    const selectedStampId =
                                        this._loader.GetSelectedStampId();
                                    if (selectedStampId) {
                                        await this.addStampTopLeft(selectedStampId);
                                    }
                                },
                            }),
                            catchError(() => of([])),
                        ),
                    ]);
                }),
            )
            .subscribe({
                error: (err) => this._logger.logException(err),
            });
    }

    public onDragTextStart(e: DragEvent, item: TextItem) {
        e.dataTransfer.setData('application/json', JSON.stringify(item));
    }

    @HostListener('window:pagehide')
    public beforeUnload() {
        if (window.opener) {
            window.opener.postMessage(
                { type: 'dispose', name: window.name },
                { targetOrigin: '*' },
            );
            window.opener = null;
        }
    }

    @HostListener('window:resize')
    @HostListener('window:orientat')
    public onSizeChanged() {
        this._pdfUi.redraw().catch((e) => this._logger.logException(e));
    }

    private _getAppearance(useCustomButtons: boolean) {
        const savePdf = this._savePdf.bind(this);
        let isLightView = localStorage.getItem('lightweightView') === '1';

        UIExtension.PDFUI.module('custom', []).controller('customController', {
            handle() {
                const root = this.component.getRoot();

                const contextmenuItems = root.querySelectorAll(
                    'fv--page-contextmenu>@contextmenu-item',
                );

                contextmenuItems.forEach(function (contextmenu: {
                    element: { style: { cssText: string } };
                }) {
                    contextmenu.element.style.cssText += 'color: red';
                });
            },
        } as Foxit.Controller);

        return UIExtension.appearances.AdaptiveAppearance.extend({
            beforeMounted: (_rootComponent: any) => {
                this.alterMode(isLightView);
            },
            getDefaultFragments: () => {
                return (
                    useCustomButtons
                        ? [
                              {
                                  target: 'hand-tool',
                                  action: UIExtension.UIConsts.FRAGMENT_ACTION.BEFORE,
                                  template:
                                      '<ribbon-button name="upload-to-server-button" tooltip-title="Save & Close" icon-class="icon-upload" >Save & Close</ribbon-button>',
                                  config: [
                                      {
                                          target: 'upload-to-server-button',
                                          callback() {
                                              this.getPDFUI()
                                                  .getPDFViewer()
                                                  .then(
                                                      (pdfViewer: {
                                                          activeElement: (
                                                              arg0: null,
                                                          ) => void;
                                                          currentPDFDoc: {
                                                              getStream: (
                                                                  arg0: ({
                                                                      arrayBuffer,
                                                                  }: {
                                                                      arrayBuffer: any;
                                                                  }) => void,
                                                              ) => Promise<any>;
                                                              getFileName: () => string;
                                                          };
                                                      }) => {
                                                          pdfViewer.activeElement(null);
                                                          const buffers =
                                                              [] as ArrayBuffer[];
                                                          return pdfViewer.currentPDFDoc
                                                              .getStream(
                                                                  ({ arrayBuffer }) => {
                                                                      buffers.push(
                                                                          arrayBuffer as ArrayBuffer,
                                                                      );
                                                                  },
                                                              )
                                                              .then(() => {
                                                                  return {
                                                                      buffers,
                                                                      fileName:
                                                                          pdfViewer.currentPDFDoc.getFileName() as string,
                                                                  };
                                                              });
                                                      },
                                                  )
                                                  .then(
                                                      (r: {
                                                          buffers: ArrayBuffer[];
                                                          fileName: string;
                                                      }) => {
                                                          if (
                                                              !r.fileName ||
                                                              r.fileName.length === 0
                                                          ) {
                                                              r.fileName = 'unknown.pdf';
                                                          }
                                                          return new File(
                                                              r.buffers,
                                                              r.fileName,
                                                              {
                                                                  type: 'application/pdf',
                                                              },
                                                          );
                                                      },
                                                  )
                                                  .then((file: any) => {
                                                      savePdf(file);
                                                  });
                                          },
                                      },
                                  ],
                              },
                              {
                                  target: 'open-file-button-list',
                                  action: UIExtension.UIConsts.FRAGMENT_ACTION.BEFORE,
                                  template:
                                      '<ribbon-button name="merge-document-button" tooltip-title="Append document" icon-class="icon-merge">Select PDF</ribbon-button>',
                                  config: [
                                      {
                                          target: 'merge-document-button',
                                          callback: () => {
                                              return this._fileSelectorService
                                                  .getFiles({
                                                      acceptedTypes: '.pdf',
                                                      multiple: false,
                                                  })
                                                  .then((f) => {
                                                      const file = f
                                                          .splice(-1)
                                                          .pop()?.file;
                                                      this.appendDoc(file);
                                                  });
                                          },
                                      },
                                  ],
                              },
                          ]
                        : []
                ).concat([
                    {
                        target: 'fv--search-bar',
                        action: UIExtension.UIConsts.FRAGMENT_ACTION.BEFORE,
                        template: `
                                <group style="flex-grow: 1;justify-content: end;" name="toolbar-light-checkbox-group" class="fv__ui-search-bar">
                                    <input type="checkbox" ${isLightView ? 'checked' : ''} name="lightview-checkbox" @on.change='callbackfn'>
                                    <label for="lightview-checkbox">Light Toolbar</label>
                                </group>
                                `,
                        config: [
                            {
                                target: 'toolbar-light-checkbox-group',
                                callback: async () => {
                                    const cmp =
                                        await this._pdfUi.getComponentByName(
                                            'lightview-checkbox',
                                        );

                                    isLightView = !isLightView;
                                    (cmp.element as HTMLInputElement).checked =
                                        !!isLightView;
                                    this.alterMode(isLightView);
                                    localStorage.setItem(
                                        'lightweightView',
                                        isLightView ? '1' : '0',
                                    );
                                },
                            },
                        ],
                    },
                ]);
            },
        } as Partial<Foxit.Appearance>);
    }

    private async alterMode(isLightToolbar: boolean) {
        const components = [
            'view-tab-group-read-aloud',
            'view-tab-comparison',
            'comment-tab',
            'view-tab',
            'form-tab',
            'protect-tab',
            'portfolio-tab',
            'protect-toolbar-group-list',
            'create-shape-dropdown',
            'create-link',
            'doc-attachment-button',
            'create-image',
            'multi-media-btn',
            'comment-tab-group-media',
            'edit-object-ribbon-dropdown',
            'join-split',
            'find-replace',
            'edit-tab-group-editor',
        ] as compName[];
        for (const el of components) {
            await this._toggleComponentVisibility(el, isLightToolbar);
        }

        const redactLight = await this._pdfUi.getComponentByName('redactionLight');
        redactLight?.remove();

        if (isLightToolbar) {
            const editTab = await this._pdfUi.getComponentByName(
                'adv-edit-tab-group-mode' as compName,
            );
            editTab.after(
                `<group name="redactionLight" functional-module="redaction"
          >
            <stamp-ribbon-dropdown></stamp-ribbon-dropdown>
            <stamp-create-ribbon-dropdown></stamp-create-ribbon-dropdown>
            <redaction:create-redactions-ribbon-dropdown></redaction:create-redactions-ribbon-dropdown>
          </group>`,
                [],
            );
        }
        (await this._pdfUi.getComponentByName('home-tab' as compName)).active();
        (await this._pdfUi.getComponentByName('hand-tool' as compName)).active();
        this._pdfUi.redraw();
    }

    private async _initPdfUi(addButtons: boolean) {
        if (this._pdfUi) {
            return false;
        }

        this._pdfUi = new UIExtension.PDFUI({
            viewerOptions: {
                libPath: '/foxit-lib/',
                jr: {
                    readyWorker: this._readyWorker,
                },
                messageSyncServiceWorker: {
                    url: '/MessageWorker.js',
                    options: {
                        scope: '/',
                    },
                },
            } as ConstructorParameters<typeof PDFViewCtrl.PDFViewer>[0],
            renderTo: this._element.nativeElement,
            appearance: this._getAppearance(addButtons),
            addons: Addons,
        });
        this._pdfUi.setJRFontMap(fontMaps);

        const pdfViewer = await this._pdfUi.getPDFViewer();
        pdfViewer.setUserName('JetFile');
        pdfViewer.addFontMaps(fontMapsInfo, null);
        pdfViewer.setEnableJS(false);
        const ViewerEvents = UIExtension.PDFViewCtrl.constants.ViewerEvents;
        const DataEvents = PDFViewCtrl.PDF.constant.DataEvents;

        this._pdfUi.addViewerEventListener(
            ViewerEvents.renderFileSuccess,
            (_pdfDoc: any) => {
                (this._pdfUi as any).addUIEventListener(
                    DataEvents.annotationAdded,
                    (annots: any) => {
                        for (const ann of annots) {
                            const annotType = ann.getType();
                            if (annotType === 'redact') {
                                ann.setFillColor('#ffffff');
                                ann.setOpacity(0.4, true);
                                ann.setRedactApplyFillColor('#ffffff');
                            }
                        }
                    },
                );
            },
        );

        (this._pdfUi as any).addViewerEventListener(ViewerEvents.jrLicenseSuccess, () => {
            const store = (pdfViewer as any).getStore();
            store.subscribe('module-license-info', (i) => {
                console.log('License info:', i);
            });
        });

        pdfViewer.setDefaultAnnotConfig((_type) => {
            const config = {
                borderInfo: {},
                defaultAppearance: {},
                noPopup: true,
                subject: '',
                title: '',
            };
            return config;
        });

        this._addDnDText(pdfViewer);
        this._addDndFile(pdfViewer);

        await this._pdfUi.waitForInitialization();

        this._fontDropdownSorter.sortFontDropdownOnAppear();
        this._turnOffControls();

        return true;
    }

    private _turnOffControls() {
        this._toggleComponentVisibility('snapshot-button', true);
        this._toggleComponentVisibility('change-color-dropdown', true);
    }

    private async _addDndFile(pdfViewer: PDFViewCtrl.PDFViewer) {
        const cmp = await this._pdfUi.getComponentByName('body' as compName);
        this.renderer2.listen(pdfViewer.element, 'drop', (e: DragEvent) => {
            const dt = e.dataTransfer;
            if (dt.files.length > 0 && dt.files[0].type === 'application/pdf') {
                this._processDrop(dt.files[0]);
                e.preventDefault();
            }
        });
        this.renderer2.listen(pdfViewer.element, 'dragover', (e: DragEvent) => {
            const dt = e.dataTransfer;
            if (
                dt.types.includes('Files') &&
                dt.items.length > 0 &&
                dt.items[0].type === 'application/pdf'
            ) {
                dt.effectAllowed = 'copy';
                e.preventDefault();
            }
        });
    }

    private _addDnDText(pdfViewer: PDFViewCtrl.PDFViewer) {
        this.renderer2.listen(pdfViewer.element, 'dragover', (e: DragEvent) => {
            if (e.dataTransfer.types.includes('application/json')) {
                e.dataTransfer.dropEffect = 'copy';

                e.preventDefault();
                e.stopPropagation();
            }
        });
        this.renderer2.listen(pdfViewer.element, 'drop', (e: DragEvent) => {
            (async () => {
                if (e.dataTransfer.types.includes('application/json')) {
                    const hand = await this._pdfUi.getComponentByName(
                        'hand-tool' as compName,
                    );
                    await hand.active();
                    await hand.controller.handle();

                    const textItemToInsert = JSON.parse(
                        e.dataTransfer.getData('application/json'),
                    ) as TextItem;
                    let coord = await pdfViewer.convertClientCoordToPDFCoord({
                        clientX: e.clientX,
                        clientY: e.clientY,
                    });
                    const pageRend = await this._pdfUi.getPDFPageRender(coord.index);
                    const page = await pageRend.getPDFPage();

                    const bb = await page.getAnnotObjectNumAtDevicePoint(
                        [coord.left, coord.top],
                        1,
                        null,
                    );
                    if (bb > -1) {
                        const annotRender = pdfViewer.getAnnotRender(coord.index, bb);
                        const widget = annotRender
                            .getComponent()
                            .getModel() as PDFViewCtrl.PDF.annots.Widget;
                        if (
                            widget &&
                            widget.getType() ===
                                PDFViewCtrl.PDF.annots.constant.Annot_Type.widget
                        ) {
                            const ctrl = widget.getControl();

                            ctrl.getField().setValue(
                                textItemToInsert.text,
                                await ctrl.getWidgetAnnot(),
                            );
                        }
                    } else {
                        const pageEditorAddon = (await this._pdfUi.getAddonInstance(
                            'PageEditor',
                        )) as unknown as Foxit.PageEditorAddon;
                        await pageEditorAddon.activate('Text');

                        let fontStandardId = undefined;
                        const style = textItemToInsert.fontStyle?.toLowerCase() ?? '';
                        const fontName = textItemToInsert.fontName;

                        const isStandardFont = ['Courier', 'Helvetica', 'Times'].includes(
                            fontName,
                        );
                        if (isStandardFont) {
                            fontStandardId = this.getFontStandardId(fontName, style);
                        }
                        let fontStyles = undefined;

                        if (style.includes('italic')) {
                            fontStyles = PDFViewCtrl.PDF.constant.Font_Styles.Italic;
                        }

                        if (style.includes('bold') && style.includes('italic')) {
                            // Foxit sdk bug: 'Bold' enum flag also applies 'Italic'
                            fontStyles = PDFViewCtrl.PDF.constant.Font_Styles.Bold;
                        }
                        const cleanedFillColor = textItemToInsert.fontColor.replace(
                            '#',
                            '',
                        );
                        const fillColor = parseInt(
                            'FF' + cleanedFillColor.toUpperCase(),
                            16,
                        );

                        const obj = await page.addGraphicsObject({
                            type: PDFViewCtrl.PDF.constant.Graphics_ObjectType.Text,
                            text: textItemToInsert.text,
                            fontSize: textItemToInsert.fontSize,
                            originPosition: { x: coord.left, y: coord.top },
                            fillColor: fillColor,
                            font: {
                                standardId: fontStandardId,
                                name: textItemToInsert.fontName,
                                styles: fontStyles,
                                charset: PDFViewCtrl.PDF.constant.Font_Charset.Default,
                            },
                        } as any);
                    }
                    e.preventDefault();
                    e.stopPropagation();
                }
            })();
        });
    }

    private getFontStandardId(fontName: string, style: string): number | undefined {
        const baseMap = {
            Courier: 'Courier',
            Helvetica: 'Helvetica',
            Times: 'Times',
        };

        const styleSuffix =
            style.includes('italic') && style.includes('bold')
                ? 'BI'
                : style.includes('italic')
                  ? 'I'
                  : style.includes('bold')
                    ? 'B'
                    : '';

        const base = baseMap[fontName as keyof typeof baseMap];
        if (!base) return undefined;

        const fontKey =
            `${base}${styleSuffix}` as keyof typeof PDFViewCtrl.PDF.constant.Font_StandardID;

        return PDFViewCtrl.PDF.constant.Font_StandardID[fontKey];
    }

    private _openPdf(file: File) {
        if (!file || file.size <= 0) {
            return Promise.resolve(null);
        }
        return this._pdfUi.openPDFByFile(file).then((d) => {
            for (const perm of [d.getPermissions(), d.getUserPermissions()]) {
                const obj = new PDFViewCtrl.UserPermission(perm);

                console.log(`Current permissions: ${perm},
                checkAnnotForm: ${obj.checkAnnotForm()}  checkAssemble: ${obj.checkAssemble()} checkCannotModifyExcludeAssemble: ${obj.checkCannotModifyExcludeAssemble()}
                checkExtract: ${obj.checkExtract()}  checkExtractAccess: ${obj.checkExtractAccess()} checkFillForm: ${obj.checkFillForm()} checkModify: ${obj.checkModify()}
                checkPrint: ${obj.checkPrint()} checkPrintHigh: ${obj.checkPrintHigh()}`);
            }
            return d;
        });
    }

    private _savePdf(file: File) {
        this._loader.SavePdf(file).subscribe({
            complete: () => window.close(),
            error: (err) => this._logger.logException(err),
        });
    }

    private _processDrop(pdfFile: File) {
        (this._pdfUi as any)
            .confirm(
                'Please, confirm that you want to open PDF. Reject if you want to append PDF instead.',
                'Confirm',
            )
            .then(() => this._pdfUi.openPDFByFile(pdfFile))
            .catch((er) => {
                this._logger.logException(er);
                this.appendDoc(pdfFile);
            });
    }

    private async _toggleComponentVisibility(componentName: compName, hide: boolean) {
        const component = await this._pdfUi.getComponentByName(componentName);
        if (!component) {
            console.error(`Component '${componentName}' was not found`);
        }
        if (hide) {
            // component.deactive();
            component.hide();
            component.keepState();
        } else {
            component.revokeKeepState();
            component.show();
        }
    }

    private async addStampTopLeft(stampId: string) {
        const pdfViewer = await this._pdfUi.getPDFViewer();
        const pdfDoc = await pdfViewer.getCurrentPDFDoc();
        const page = await pdfDoc.getPageByIndex(0);

        const iconsMap = (await this._pdfUi.getAnnotationIcons('stamp', false)) as Record<
            string,
            Record<string, any>
        >;

        const availableIcons = Object.entries(iconsMap).flatMap(([category, iconMap]) =>
            Object.entries(iconMap).map(([name, icon]) => ({
                ...icon,
                category,
                name,
            })),
        );

        const stampInfo = availableIcons.find((x) => x.stampId === stampId);
        if (!stampInfo) throw new Error(`Stamp with id ${stampId} not found`);

        const pageHeight = await page.getHeight();
        const stampWidth = stampInfo.width;
        const stampHeight = stampInfo.height;

        const left = 0;
        const top = pageHeight;
        const right = left + stampWidth;
        const bottom = top - stampHeight;

        const stampAnnot = {
            type: PDFViewCtrl.PDF.annots.constant.Annot_Type.stamp,
            rect: { left, bottom, right, top },
            iconInfo: {
                annotType: PDFViewCtrl.PDF.annots.constant.Annot_Type.stamp,
                category: stampInfo.category,
                name: stampInfo.name,
                fileType: stampInfo.fileType,
                url: stampInfo.url,
            },
        };

        await page.addAnnot(stampAnnot);
    }
}

const componentList = [
    'All',
    'Curve',
    'Ellipse',
    'Freedom',
    'Image',
    'Line',
    'Path',
    'Polybezier',
    'Polygon',
    'Polyline',
    'Rectangle',
    'RoundRect',
    'Shading',
    'Text',
    'add-custom-stamp',
    'add-pathObject-circle',
    'add-pathObject-line',
    'add-pathObject-roundRect',
    'add-pathObject-square',
    'addon-textobject-color-picker',
    'adv-edit-tab-group-mode',
    'append-to-sheet',
    'body',
    'change-color-dropdown',
    'comment-list-sidebar-panel',
    'comment-tab',
    'comment-tab-group-drawing',
    'comment-tab-group-hand',
    'comment-tab-group-import-export',
    'comment-tab-group-inksign',
    'comment-tab-group-mark',
    'comment-tab-group-measurement',
    'comment-tab-group-media',
    'comment-tab-group-note',
    'comment-tab-group-other',
    'comment-tab-group-pencil',
    'comment-tab-group-stamp',
    'comment-tab-group-text',
    'comment-toolbar-group-list',
    'commentlist-export-comment',
    'commentlist-filter-author-list',
    'commentlist-filter-original-author',
    'commentlist-filter-status-list',
    'commentlist-filter-title-author',
    'commentlist-filter-title-status',
    'commentlist-filter-title-type',
    'commentlist-filter-type-list',
    'commentlist-import-comment',
    'comparison-tab',
    'contextmenu-form-designer',
    'continuous-facing-page',
    'continuous-page',
    'create-area-btn',
    'create-area-highlight',
    'create-arrow',
    'create-caret',
    'create-circle',
    'create-circle-area-btn',
    'create-cloud',
    'create-custom-dynamic-stamp',
    'create-custom-stamps',
    'create-digital-stamp-dropdown',
    'create-digital-stamp-list',
    'create-distance-btn',
    'create-fileattachment',
    'create-highlight',
    'create-image',
    'create-line',
    'create-link',
    'create-measurement-button-list',
    'create-page-redaction',
    'create-perimeter-btn',
    'create-polygon',
    'create-polyline',
    'create-redaction-controllers',
    'create-redactions-ribbon-inner-dropdown',
    'create-replace',
    'create-shape-dropdown',
    'create-shapeObject-dropdown',
    'create-signature',
    'create-signature-btn',
    'create-square',
    'create-squiggly',
    'create-stamp-button-list',
    'create-stamp-dialog',
    'create-stamp-drop-down-ui',
    'create-stamp-dropdown',
    'create-stamp-handle-ui',
    'create-strikeout',
    'create-text',
    'create-text-image-redaction',
    'create-underline',
    'custom-stamps-list',
    'doc-attachment-button',
    'download-file-button',
    'dragger',
    'dropdown-adjusted-read-rate',
    'dropdown-adjusted-read-volume',
    'dropdown-pause-read',
    'dropdown-read-current-page',
    'dropdown-read-from-current-page',
    'dropdown-stop-read',
    'dropdown-zoom',
    'dropdown-zoom-fitpage',
    'dropdown-zoom-fitvisible',
    'dropdown-zoom-fitwidth',
    'dropdown-zoom-in',
    'dropdown-zoom-out',
    'edit-all-objects',
    'edit-object-ribbon-dropdown',
    'edit-tab',
    'edit-tab-group-editor',
    'edit-tab-group-font',
    'edit-tab-group-hand',
    'edit-tab-group-layer',
    'edit-tab-group-mode',
    'edit-tab-group-redact',
    'edit-text',
    'edit-toolbar-group-list',
    'editable-zoom-dropdown',
    'editable-zoom-dropdown-fitpage',
    'editable-zoom-dropdown-fitvisible',
    'editable-zoom-dropdown-fitwidth',
    'eraser-tool',
    'export-comment-dropdown-container',
    'export-to-csv',
    'export-to-fdf',
    'export-to-txt',
    'export-to-xfdf',
    'export-to-xml',
    'facing-page',
    'file-property',
    'file-property-btn',
    'file-property-popup',
    'find-replace',
    'font-family-dropdown',
    'font-size-dropdown',
    'form-tab',
    'form-tab-group-assistants',
    'form-tab-group-fields',
    'form-tab-group-hand',
    'form-tab-group-import-export',
    'form-tab-group-tools',
    'form-to-sheet',
    'form-toolbar-group-list',
    'freetext-callout',
    'freetext-textbox',
    'freetext-typewriter',
    'fv--add-tooltip-btn',
    'fv--addon-add-text-button',
    'fv--addon-textobject-bold-style',
    'fv--addon-textobject-font-color',
    'fv--addon-textobject-font-family-size-dropdown',
    'fv--addon-textobject-italic-style',
    'fv--annottext-tooltip',
    'fv--areahighlight-contextmenu',
    'fv--bookmark-contextmenu',
    'fv--bookmark-contextmenu-v2',
    'fv--callout-contextmenu',
    'fv--caret-contextmenu',
    'fv--circle-contextmenu',
    'fv--comment-card-contextmenu',
    'fv--comment-tab-paddle',
    'fv--commentlist-export-comment-dropdown',
    'fv--commentlist-filter-cancel-btn',
    'fv--commentlist-filter-clear-selection-btn',
    'fv--commentlist-filter-comment-dialog',
    'fv--commentlist-filter-ok-btn',
    'fv--commentlist-import-comment-dropdown',
    'fv--commentlist-import-comment-ribbon-button',
    'fv--comparison-options-dialog',
    'fv--comparison-select-pdf-file-dialog',
    'fv--comparison-tab-body',
    'fv--contextmenu-item-bring-back',
    'fv--contextmenu-item-bring-backward',
    'fv--contextmenu-item-bring-forward',
    'fv--contextmenu-item-bring-front',
    'fv--contextmenu-item-default-prop',
    'fv--contextmenu-item-delete-annot',
    'fv--contextmenu-item-file-property',
    'fv--contextmenu-item-full-screen',
    'fv--contextmenu-item-group',
    'fv--contextmenu-item-hand-tool',
    'fv--contextmenu-item-marquee-zoom',
    'fv--contextmenu-item-print',
    'fv--contextmenu-item-properties',
    'fv--contextmenu-item-reply',
    'fv--contextmenu-item-rotate-left',
    'fv--contextmenu-item-rotate-right',
    'fv--contextmenu-item-select-annotation',
    'fv--contextmenu-item-select-text-image',
    'fv--contextmenu-item-zoom-actual-size',
    'fv--contextmenu-item-zoom-fitpage',
    'fv--contextmenu-item-zoom-fitvisible',
    'fv--contextmenu-item-zoom-fitwidth',
    'fv--creating-drawing-contextmenu',
    'fv--creating-measurement-contextmenu',
    'fv--custom-dynamic-stamp-dialog',
    'fv--default-annot-contextmenu',
    'fv--edit-tab-paddle',
    'fv--export-fdf-button',
    'fv--export-form-dropdown',
    'fv--export-json-button',
    'fv--export-xfdf-button',
    'fv--field-contextmenu',
    'fv--field-signature-contextmenu',
    'fv--fileattachment-contextmenu',
    'fv--filter-option-by-original-author',
    'fv--filter-option-states',
    'fv--form-designer-assistant',
    'fv--form-designer-create-check-box-btn',
    'fv--form-designer-create-combo-box-btn',
    'fv--form-designer-create-date-btn',
    'fv--form-designer-create-image-btn',
    'fv--form-designer-create-list-box-btn',
    'fv--form-designer-create-push-button-btn',
    'fv--form-designer-create-radio-button-btn',
    'fv--form-designer-create-sign-btn',
    'fv--form-designer-create-text-btn',
    'fv--form-tab-paddle',
    'fv--graphics-object-properties-dialog',
    'fv--highlight-contextmenu',
    'fv--home-tab-paddle',
    'fv--identity-info-dialog',
    'fv--image-contextmenu',
    'fv--image-graphics-object-contextmenu',
    'fv--ink-contextmenu',
    'fv--ink-sign-dropdown',
    'fv--inner-zoom-ribbon-dropdown',
    'fv--line-contextmenu',
    'fv--linearrow-contextmenu',
    'fv--linedimension-contextmenu',
    'fv--link-contextmenu',
    'fv--markup-contextmenu',
    'fv--media-contextmenu',
    'fv--page-contextmenu',
    'fv--path-graphics-object-contextmenu',
    'fv--plugin-PageEditor-add-image',
    'fv--plugin-PageEditor-add-text',
    'fv--polygon-contextmenu',
    'fv--polygoncloud-contextmenu',
    'fv--polygondimension-contextmenu',
    'fv--polyline-contextmenu',
    'fv--polylinedimention-contextmenu',
    'fv--portfolio-block-layout-button',
    'fv--portfolio-cover-sheet-layout-button',
    'fv--portfolio-default-block-layout-button',
    'fv--portfolio-default-cover-sheet-layout-button',
    'fv--portfolio-default-details-layout-button',
    'fv--portfolio-default-layout-dropdown',
    'fv--portfolio-default-original-layout-button',
    'fv--portfolio-details-layout-button',
    'fv--portfolio-file-item-contextmenu',
    'fv--portfolio-loading-layer-inner',
    'fv--portfolio-preview-bottom-side-button',
    'fv--portfolio-preview-off-button',
    'fv--portfolio-preview-right-side-button',
    'fv--portfolio-previewer-container',
    'fv--portfolio-tab-body',
    'fv--protect-tab-paddle',
    'fv--read-aloud-rate-slider-container',
    'fv--read-aloud-volume-slider-container',
    'fv--redact-contextmenu',
    'fv--replace-contextmenu',
    'fv--reset-field-btn',
    'fv--search-bar',
    'fv--search-bar-input',
    'fv--search-bar-type-list-button',
    'fv--search-basic-options',
    'fv--search-basic-options-case-sensitive',
    'fv--search-basic-options-highlight',
    'fv--search-basic-options-whole-word',
    'fv--search-choose-search-type',
    'fv--search-choose-type-advanced',
    'fv--search-choose-type-highlight',
    'fv--search-choose-type-redact',
    'fv--search-input-button-clear',
    'fv--search-input-button-down',
    'fv--search-input-button-group',
    'fv--search-input-button-up',
    'fv--search-input-input',
    'fv--search-options-button',
    'fv--selected-image-contextmenu',
    'fv--selected-rectangle-contextmenu',
    'fv--selected-text-contextmenu',
    'fv--showCO-btn',
    'fv--sound-contextmenu',
    'fv--square-contextmenu',
    'fv--squiggly-contextmenu',
    'fv--stamp-contextmenu',
    'fv--strikeout-contextmenu',
    'fv--text-contextmenu',
    'fv--text-selection-tooltip',
    'fv--textbox-contextmenu',
    'fv--thumbnail-contextmenu',
    'fv--typewriter-contextmenu',
    'fv--underline-contextmenu',
    'fv--view-tab-paddle',
    'fv--xfa-signature-contextmenu',
    'fv--xfa-signature-contextmenu-show-properties',
    'fv--xfa-signature-contextmenu-sign',
    'fv-layer-sidebar-panel',
    'fv__ui-comparison-legend-layer',
    'fv__ui-comparison-progress-layer',
    'goto-first-page',
    'goto-last-page',
    'goto-next-page',
    'goto-prev-page',
    'gotoPage',
    'gotopage-input',
    'gotopage-menu-container',
    'h-continuous-button',
    'hand-tool',
    'highlight-fields-checkbox',
    'home-tab',
    'home-tab-group-change-color',
    'home-tab-group-hand',
    'home-tab-group-io',
    'home-tab-group-nav',
    'home-tab-group-page',
    'home-tab-group-zoom',
    'home-toolbar-group-list',
    'image-tool',
    'import-form-tool',
    'join-split',
    'keep-tool-selected-checkbox',
    'lightview-checkbox',
    'loupe-button',
    'loupe-tool-dialog',
    'marquee-button',
    'merge-document-button',
    'multi-media',
    'multi-media-btn',
    'open-file-button-list',
    'open-from-url',
    'open-local-file',
    'page-template-button',
    'page-template-dialog',
    'password-protect-btn',
    'password-protect-controllers',
    'password-protect-group',
    'path',
    'pdf-viewer',
    'pdf-viewer-container-with-ruler',
    'pencil-tool',
    'portfolio-container',
    'portfolio-default-layout-dropdown',
    'portfolio-preview-side-dropdown',
    'portfolio-sidebar',
    'portfolio-tab',
    'portfolio-tab-group-layout-buttons',
    'portfolio-toolbar-group-list',
    'print-button',
    'print-dialog',
    'protect-tab',
    'protect-tab-group-hand',
    'protect-tab-group-sign',
    'protect-toolbar-group-list',
    'rate-dropdown',
    'read-aloud-ribbon-dropdown',
    'read-dropdown',
    'recognition-form-button',
    'redaction',
    'redaction-apply',
    'redaction-search',
    'remove-protect-btn',
    'select-annotation',
    'select-text-image',
    'selection-dropdown',
    'shading',
    'shape-drop-down-ui',
    'sidebar',
    'sidebar-attachment',
    'sidebar-bookmark-v2',
    'sidebar-field',
    'sidebar-layers',
    'sidebar-right',
    'sidebar-thumbnail-panel',
    'signature-dropdown',
    'signature-list',
    'signed-property-dialog',
    'single-page',
    'snapshot-button',
    'stamp-drop-down-ui',
    'stamp-ribbon-dropdown',
    'template-container',
    'toolbar',
    'toolbar-light-checkbox-group',
    'toolbar-tab-bodies',
    'toolbar-tabs',
    'upload-to-server-button',
    'view-compare-button',
    'view-tab',
    'view-tab-comparison',
    'view-tab-group-assistants',
    'view-tab-group-read-aloud',
    'view-toolbar-group-list',
    'volume-dropdown',
    'xfa-form-button',
    'zoom-in',
    'zoom-out',
] as const;
type compName = string & (typeof componentList)[keyof typeof componentList];
